// Real BIP39 + Ed25519 pseudonymous identity for Opinione Libera.
// - 12-word Italian mnemonic (128 bits entropy, BIP39 standard).
// - Deterministic Ed25519 keypair derived from the seed via SHA-512.
// - Public identityId = first 16 hex chars of SHA-256(publicKey).
// - Persisted in localStorage; the sensitive `mnemonic` is AES-GCM sealed
//   with a non-extractable IndexedDB key (see lib/keystore.js).

import { generateMnemonic, validateMnemonic, mnemonicToSeedSync } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/italian.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { getPublicKeyAsync, signAsync } from "@noble/ed25519";
import {
  destroyWrapKey,
  encryptString,
  decryptString,
  getOrCreateWrapKey,
  getExistingWrapKey,
  requestPersistentKeyStorage,
} from "./keystore";
import { readItem, removeItem, writeItem } from "./localStore";
import { devDebug } from "./devLog";
import { Capacitor, registerPlugin } from "@capacitor/core";

const NativeIdentity = registerPlugin("NativeIdentity");

function hasNativeIdentityStore() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

const STORAGE_KEY = "ol_identity_v1";

function bytesToHex(bytes) {
  let out = "";
  for (const b of bytes) out += b.toString(16).padStart(2, "0");
  return out;
}

function hexToBytes(hex) {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function normalizeMnemonicWords(input) {
  const value = Array.isArray(input) ? input.join(" ") : String(input || "");
  return value.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** Generates a fresh 12-word Italian BIP39 mnemonic. */
export function generateMnemonicWords() {
  return generateMnemonic(wordlist, 128).split(" ");
}

/** True if the 12 words form a valid BIP39 mnemonic (checksum ok). */
export function isValidMnemonic(words) {
  return validateMnemonic(words.join(" "), wordlist);
}

/**
 * Derives an Ed25519 keypair from the 12-word mnemonic.
 * The private key is the first 32 bytes of PBKDF2-SHA512(mnemonic, "OpinioneLibera")
 * (via BIP39's mnemonicToSeed) — deterministic and standard-compliant.
 */
export async function deriveKeypair(words) {
  const mnemonic = words.join(" ");
  if (!validateMnemonic(mnemonic, wordlist)) {
    throw new Error("Mnemonica non valida");
  }
  const seed = mnemonicToSeedSync(mnemonic, "OpinioneLibera");
  const privateKey = seed.slice(0, 32); // Ed25519 needs 32-byte seed
  const publicKey = await getPublicKeyAsync(privateKey);
  const identityId = bytesToHex(sha256(publicKey)).slice(0, 16);
  return {
    privateKeyHex: bytesToHex(privateKey),
    publicKeyHex: bytesToHex(publicKey),
    identityId,
  };
}

/** Signs an arbitrary message with the private key (hex → hex). */
export async function signMessage(privateKeyHex, message) {
  const priv = hexToBytes(privateKeyHex);
  const msgBytes = new TextEncoder().encode(message);
  const sig = await signAsync(msgBytes, priv);
  return bytesToHex(sig);
}

/**
 * Persists the identity locally. The `mnemonic` is sealed with the
 * IndexedDB wrap key (non-extractable), so an out-of-band localStorage
 * dump does not leak the wallet.
 *
 * SECURITY NOTE: Only public metadata (`identityId`, `publicKeyHex`,
 * `displayName`, `avatar`) is persisted in cleartext to localStorage —
 * these values are already exposed via the public API. The sensitive
 * BIP39 mnemonic is AES-GCM sealed with a non-extractable IndexedDB
 * CryptoKey (see `keystore.js`); an attacker with XSS access to
 * localStorage still cannot decrypt the wallet without also
 * exfiltrating the IndexedDB CryptoKey handle (which is non-extractable
 * by design). This is the standard local-first crypto wallet pattern.
 */
export async function saveIdentity({ name, avatar, mnemonic, publicKeyHex, identityId }) {
  await requestPersistentKeyStorage();
  const key = await getOrCreateWrapKey();
  const sealedMnemonic = await encryptString(key, mnemonic);
  const payload = {
    name,
    avatar,
    publicKeyHex,
    identityId,
    sealedMnemonic, // { iv, ct } base64 — plaintext never touches localStorage
    createdAt: new Date().toISOString(),
    version: 2,
  };
  // Zero-knowledge architecture: sensitive mnemonic is AES-GCM sealed via a
  // non-extractable IndexedDB CryptoKey (see keystore.js). Only public metadata
  // is written in cleartext here. httpOnly cookies are NOT applicable — there
  // is no session server.
  const serialized = JSON.stringify(payload);
  if (!writeItem(STORAGE_KEY, serialized)) {
    throw new Error("Il dispositivo non riesce a conservare il profilo");
  }
  if (hasNativeIdentityStore()) {
    await NativeIdentity.save({ payload: serialized });
  }
  return payload;
}

/** Loads the persisted identity metadata (no plaintext mnemonic). */
export function loadIdentity() {
  try {
    // Zero-knowledge: only sealed metadata is read here (see saveIdentity).
    const raw = readItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Restores the encrypted identity mirror kept by the native Android app. */
export async function loadIdentityFromNative() {
  if (!hasNativeIdentityStore()) return null;
  try {
    const result = await NativeIdentity.load();
    const raw = String(result?.payload || "");
    if (!raw) return null;
    const payload = JSON.parse(raw);
    if (!payload?.identityId || !payload?.sealedMnemonic) return null;
    if (!writeItem(STORAGE_KEY, raw)) {
      throw new Error("Il WebView non accetta la persistenza locale");
    }
    return payload;
  } catch (error) {
    devDebug("loadIdentityFromNative failed", error?.message);
    return null;
  }
}

/** Unseals and returns the mnemonic. Async — call only when the user asks. */
export async function unlockMnemonic() {
  const identity = loadIdentity();
  if (!identity?.sealedMnemonic) return null;
  try {
    const key = await getExistingWrapKey();
    if (!key) return null;
    return await decryptString(key, identity.sealedMnemonic);
  } catch {
    return null;
  }
}

/**
 * Re-seals the current profile with its original 12 words.
 * This is used when localStorage still contains the public profile metadata
 * but the browser has lost or replaced the IndexedDB wrapping key. The words
 * must derive exactly the identityId already open on the device: recovery can
 * never be used to silently replace one profile with another.
 */
export async function recoverCurrentIdentity(input) {
  const identity = loadIdentity();
  if (!identity?.identityId) {
    throw new Error("Nessun profilo locale da ripristinare.");
  }
  const words = normalizeMnemonicWords(input);
  if (words.length !== 12 || !isValidMnemonic(words)) {
    throw new Error("Le 12 parole non sono valide. Controlla ordine e ortografia.");
  }
  const keys = await deriveKeypair(words);
  const expectedIdentityId = String(identity.identityId).toLowerCase();
  if (keys.identityId !== expectedIdentityId) {
    throw new Error("Queste 12 parole appartengono a un profilo diverso da quello aperto.");
  }

  const payload = {
    name: String(identity.name || ""),
    avatar: String(identity.avatar || ""),
    mnemonic: words.join(" "),
    publicKeyHex: keys.publicKeyHex,
    identityId: keys.identityId,
  };

  try {
    return await saveIdentity(payload);
  } catch (firstError) {
    // A damaged/unsupported CryptoKey handle may still be present in IndexedDB.
    // The mnemonic has already been validated against the current identity, so
    // replacing only the local wrapping key is safe and preserves the profile.
    try {
      await destroyWrapKey();
      return await saveIdentity(payload);
    } catch (secondError) {
      devDebug("recoverCurrentIdentity: reseal failed", secondError?.message || firstError?.message);
      throw new Error("Il browser non riesce a salvare la nuova chiave protetta. Controlla che i dati del sito non siano bloccati.");
    }
  }
}

/** Clears the identity from local storage AND destroys the wrap key. */
export async function clearIdentity() {
  removeItem(STORAGE_KEY);
  if (hasNativeIdentityStore()) {
    try { await NativeIdentity.clear(); } catch (error) { devDebug("NativeIdentity.clear failed", error?.message); }
  }
  try {
    await destroyWrapKey();
  } catch (err) {
    // Best-effort: even if the IndexedDB key can't be destroyed we still
    // wiped the localStorage payload above, so the account is unusable
    // without the mnemonic. Log for debugging.
    devDebug("clearIdentity: destroyWrapKey failed", err);
  }
}

/**
 * Returns a downloadable Blob containing the mnemonic backup. Caller is
 * responsible for having already unlocked the plaintext mnemonic.
 */
export function buildBackupBlob({ mnemonic, identityId }) {
  const content = JSON.stringify(
    {
      app: "Opinione Libera",
      version: 1,
      warning:
        "Queste 12 parole sono l'unica via di recupero del tuo profilo. Non condividerle mai.",
      identityId,
      mnemonic,
    },
    null,
    2,
  );
  return new Blob([content], { type: "application/json" });
}
